/**
 * Render the @cairn/core condition DSL as a compact human string for the
 * read-only routing/readiness admin views (§8.2/§9.2). The DSL arrives over the
 * wire as `unknown`, so this is fully defensive. Pure → unit-tested in node.
 *
 * Shapes: { field, op, value } | { all: [...] } | { any: [...] } | { not: cond }.
 * (`and`/`or` accepted as aliases for resilience against older payloads.)
 */
const OP_LABEL: Record<string, string> = {
  eq: "=",
  neq: "≠",
  ne: "≠",
  in: "in",
  nin: "not in",
  exists: "exists",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  contains: "contains",
  startsWith: "starts with",
};

function val(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(val).join(", ")}]`;
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

export function describeCondition(cond: unknown): string {
  if (cond == null || typeof cond !== "object") return "always";
  const o = cond as Record<string, unknown>;

  const allArr = Array.isArray(o.all) ? o.all : Array.isArray(o.and) ? o.and : null;
  if (allArr) {
    const parts = allArr.map(describeCondition).filter(Boolean);
    return parts.length ? parts.join(" AND ") : "always";
  }
  const anyArr = Array.isArray(o.any) ? o.any : Array.isArray(o.or) ? o.or : null;
  if (anyArr) {
    const parts = anyArr.map(describeCondition).filter(Boolean);
    return parts.length ? `(${parts.join(" OR ")})` : "always";
  }
  if (o.not != null) return `NOT ${describeCondition(o.not)}`;

  if (typeof o.field === "string") {
    const op = typeof o.op === "string" ? o.op : "eq";
    if (op === "exists") return `${o.field} exists`;
    const label = OP_LABEL[op] ?? op;
    return `${o.field} ${label} ${val(o.value)}`;
  }
  return "always";
}
